import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = BASE;
// Anonymous device id (also used by analytics) so free-preview time survives refreshes.
export const visitorId = () => {
  let v = localStorage.getItem("bbk_vid");
  if (!v) { v = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}${Math.random()}`).replace(/[^A-Za-z0-9]/g, "").slice(0, 32); localStorage.setItem("bbk_vid", v); }
  return v;
};
export const api = axios.create({ baseURL: `${BASE}/api`, timeout: 20000 });

api.interceptors.request.use((config) => {
  const t = localStorage.getItem("bbk_token");
  if (t) config.headers.Authorization = `Bearer ${t}`;
  config.headers["X-Visitor-Id"] = visitorId();
  return config;
});

// ONE DEVICE rule: if the server revokes this session (login elsewhere) or the token is
// invalid/expired, drop the token and notify the app so it signs out immediately.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    const url = err?.config?.url || "";
    const hadToken = !!localStorage.getItem("bbk_token");
    if (status === 401 && hadToken && !url.includes("/auth/otp") && !url.includes("/auth/google")) {
      localStorage.removeItem("bbk_token");
      window.dispatchEvent(new CustomEvent("session-revoked", { detail }));
    }
    // Resilience: transparently retry idempotent GETs on network errors / 5xx / Cloudflare 52x
    // (brief origin restarts) with backoff, so pages don't render empty on a transient blip.
    const cfg = err?.config;
    const retriable = !status || status >= 500 || status === 429;
    if (cfg && (cfg.method || "get").toLowerCase() === "get" && retriable && !cfg.noRetry) {
      cfg._retry = (cfg._retry || 0) + 1;
      if (cfg._retry <= 3) {
        return new Promise((r) => setTimeout(r, 700 * 2 ** (cfg._retry - 1))).then(() => api(cfg));
      }
    }
    return Promise.reject(err);
  }
);

const form = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return fd;
};

export const endpoints = {
  // auth
  health: () => api.get("/health"),
  otpRequest: (payload) => api.post("/auth/otp/request", payload),
  otpVerify: (payload) => api.post("/auth/otp/verify", payload),
  googleSession: (sessionId) => api.post("/auth/google/session", { sessionId }),
  me: () => api.get("/auth/me"),
  updateMe: (data) => api.patch("/auth/me", data),
  logout: () => api.post("/auth/logout"),
  session: () => api.get("/auth/session"),

  // Customer support inbox
  supportSend: (text) => api.post("/support/messages", { text }),
  supportThread: () => api.get("/support/thread"),
  supportUnread: () => api.get("/support/unread"),
  adminSupportConversations: () => api.get("/admin/support/conversations"),
  adminSupportThread: (userId) => api.get(`/admin/support/conversations/${userId}`),
  adminSupportReply: (userId, text) => api.post(`/admin/support/conversations/${userId}/reply`, { text }),
  msgTemplates: () => api.get("/admin/message-templates"),
  msgTemplateUpdate: (key, data) => api.put(`/admin/message-templates/${key}`, data),
  msgTemplateReset: (key) => api.post(`/admin/message-templates/${key}/reset`),
  msgTemplateAudit: (key) => api.get(`/admin/message-templates/${key}/audit`),
  msgLinks: () => api.get("/admin/message-links"),
  msgLinkUpdate: (key, data) => api.put(`/admin/message-links/${key}`, data),
  broadcastCounts: () => api.get("/admin/broadcast/audience-counts"),
  broadcastSend: (data) => api.post("/admin/broadcast", data),
  broadcastList: () => api.get("/admin/broadcasts"),
  broadcastStatus: (id) => api.get(`/admin/broadcast/${id}`),

  // radio / content
  nowPlaying: () => api.get("/radio/now-playing"),
  radioStream: () => api.get("/radio/stream"),
  schedule: () => api.get("/schedule"),
  settings: () => api.get("/settings"),
  journalists: () => api.get("/journalists"),
  adminJournalists: () => api.get("/admin/journalists"),
  adminCreateJournalist: (b) => api.post("/admin/journalists", b),
  adminUpdateJournalist: (id, b) => api.put(`/admin/journalists/${id}`, b),
  adminDeleteJournalist: (id) => api.delete(`/admin/journalists/${id}`),
  uploadImage: (formData) => api.post("/admin/upload/image", formData, { headers: { "Content-Type": "multipart/form-data" } }),
  shows: (limit = 40, offset = 0, extra = {}) => api.get("/shows", { params: { limit, offset, ...extra } }),
  contact: (b) => api.post("/contact", b),
  show: (id) => api.get(`/shows/${id}`),
  categories: () => api.get("/categories"),
  programs: () => api.get("/programs"),
  news: () => api.get("/news"),
  newsItem: (id) => api.get(`/news/${id}`),
  liveChannels: (force) => api.get("/live/channels", { params: force ? { refresh: true } : {} }),
  liveShows: () => api.get("/live-shows"),

  // terms
  termsCurrent: () => api.get("/terms/current"),
  termsAccept: (version) => api.post("/terms/accept", { version }),

  // billing
  plans: () => api.get("/billing/plans"),
  subStatus: () => api.get("/subscription/status"),
  history: () => api.get("/billing/history"),
  vodOwned: () => api.get("/billing/vod/owned"),
  checkout: (planId) => api.post("/billing/checkout", { planId }),
  stripeCheckout: (body) => api.post("/billing/stripe/create-checkout", body),
  stripeStatus: (sid) => api.get(`/billing/stripe/session-status/${sid}`),
  paypalCreate: (plan, currency, source) => api.post("/billing/paypal/create-subscription", { plan, currency, source }),
  paypalCapture: (orderId) => api.post("/billing/paypal/capture", { orderId }),
  paypalConfig: () => api.get("/billing/paypal/config"),
  momoInitiate: (plan, phone) => api.post("/billing/momo/initiate", { plan, phone }),
  momoStatus: (ref) => api.get(`/billing/momo/${ref}`),
  billingCurrencies: () => api.get("/billing/currencies"),
  adminCurrencies: () => api.get("/admin/currencies"),
  adminCreateCurrency: (b) => api.post("/admin/currencies", b),
  adminUpdateCurrency: (code, b) => api.put(`/admin/currencies/${code}`, b),
  adminDeleteCurrency: (code) => api.delete(`/admin/currencies/${code}`),

  // admin analytics
  adminDashboard: () => api.get("/admin/analytics/dashboard"),
  adminRevenue: () => api.get("/admin/analytics/revenue"),
  adminUsers: () => api.get("/admin/users"),
  adminPayments: () => api.get("/admin/payments"),
  adminPaymentsAll: () => api.get("/admin/payments/all"),
  adminPaymentsSummary: () => api.get("/admin/payments/summary"),

  // admin content CRUD
  adminCreate: (kind, b) => api.post(`/admin/${kind}`, b),
  adminDelete: (kind, id) => api.delete(`/admin/${kind}/${id}`),
  adminCreateShow: (b) => api.post("/admin/shows", b),
  adminDeleteShow: (id) => api.delete(`/admin/shows/${id}`),
  adminCreateNews: (b) => api.post("/admin/news", b),
  adminUpdateNews: (id, b) => api.put(`/admin/news/${id}`, b),
  adminDeleteNews: (id) => api.delete(`/admin/news/${id}`),
  adminArchiveNews: (id, archived) => api.post(`/admin/news/${id}/archive`, { archived }),
  adminCreateSchedule: (b) => api.post("/admin/schedule", b),
  adminDeleteSchedule: (id) => api.delete(`/admin/schedule/${id}`),
  adminPrograms: () => api.get("/admin/programs"),
  adminCreateProgram: (b) => api.post("/admin/programs", b),
  adminUpdateProgram: (id, b) => api.put(`/admin/programs/${id}`, b),
  adminDeleteProgram: (id) => api.delete(`/admin/programs/${id}`),
  adminCategories: () => api.get("/admin/categories"),
  adminCreateCategory: (b) => api.post("/admin/categories", b),
  adminUpdateCategory: (id, b) => api.put(`/admin/categories/${id}`, b),
  adminDeleteCategory: (id) => api.delete(`/admin/categories/${id}`),
  newsCategories: () => api.get("/news-categories"),
  adminNewsCategories: () => api.get("/admin/news-categories"),
  adminCreateNewsCategory: (b) => api.post("/admin/news-categories", b),
  adminUpdateNewsCategory: (id, b) => api.put(`/admin/news-categories/${id}`, b),
  adminDeleteNewsCategory: (id) => api.delete(`/admin/news-categories/${id}`),

  // admin settings / plans / roles / invites
  adminGetSettings: () => api.get("/admin/settings"),
  adminUpdateSettings: (b) => api.put("/admin/settings", b),
  adminSettingsSave: (b) => api.put("/admin/settings", b),
  adminUpdatePlan: (id, b) => api.put(`/admin/plans/${id}`, b),
  adminSetRole: (id, role) => api.post(`/admin/users/${id}/role`, { role }),
  adminUpdateUser: (id, b) => api.put(`/admin/users/${id}`, b),
  adminDeleteUser: (id) => api.delete(`/admin/users/${id}`),
  adminInvite: (row) => api.post("/admin/users/invite", row),
  adminBulkInvite: (rows) => api.post("/admin/users/bulk-invite", rows),

  // admin live / youtube
  adminLiveShows: () => api.get("/admin/live-shows"),
  adminCreateLiveShow: (b) => api.post("/admin/live-shows", b),
  adminEndLiveShow: (id) => api.post(`/admin/live-shows/${id}/end`),
  adminYoutubeStatus: () => api.get("/admin/youtube/status"),
  adminYoutubeConfig: () => api.get("/admin/youtube/config"),
  adminYoutubeOauthStart: () => api.get("/admin/youtube/oauth-start"),
  adminYoutubeSync: () => api.post("/admin/youtube/sync"),
  adminYoutubeImport: (b) => api.post("/admin/youtube/import", b || {}),

  // live streams (generalized, multi-source) + playback gating
  liveStreams: () => api.get("/live/streams"),
  liveStreamPlayback: (id) => api.get(`/live/streams/${id}/playback`),
  showPlayback: (id) => api.get(`/shows/${id}/playback`),
  registerView: (id) => api.post(`/shows/${id}/view`),
  adminShowsAll: () => api.get("/admin/shows"),
  adminUpdateShow: (id, b) => api.put(`/admin/shows/${id}`, b),
  adminLiveStreams: () => api.get("/admin/live-streams"),
  adminLiveStreamsHealth: () => api.get("/admin/live-streams/health"),
  adminCreateLiveStream: (b) => api.post("/admin/live-streams", b),
  adminUpdateLiveStream: (id, b) => api.put(`/admin/live-streams/${id}`, b),
  adminDeleteLiveStream: (id) => api.delete(`/admin/live-streams/${id}`),
  adminEndLiveStream2: (id, b) => api.post(`/admin/live-streams/${id}/end`, b || {}),
  adminChannels: () => api.get("/admin/channels"),
  adminCreateChannel: (b) => api.post("/admin/channels", b),
  adminUpdateChannel: (id, b) => api.put(`/admin/channels/${id}`, b),
  adminDeleteChannel: (id) => api.delete(`/admin/channels/${id}`),
  adminPaymentsReport: (params) => api.get("/admin/payments/report", { params }),

  // admin uploads
  adminUploadImage: (file) => api.post("/admin/upload/image", form(file), { headers: { "Content-Type": "multipart/form-data" } }),
  adminUploadVideo: (file, onProgress) => api.post("/admin/upload/video", form(file), { headers: { "Content-Type": "multipart/form-data" }, onUploadProgress: onProgress }),

  // admin controller / troubleshoot
  controllerUser: (ident) => api.get(`/admin/controller/user/${encodeURIComponent(ident)}`),
  controllerReconcile: (ident) => api.post(`/admin/controller/reconcile/${encodeURIComponent(ident)}`),
  controllerReceipt: (ident) => api.get(`/admin/controller/receipt/${encodeURIComponent(ident)}`, { responseType: "blob" }),

  // live engagement (chat / viewers / likes)
  liveHeartbeat: (id) => api.post(`/live/${id}/heartbeat`),
  liveEngagement: (id) => api.get(`/live/${id}/engagement`),
  liveLike: (id) => api.post(`/live/${id}/like`),
  liveChatGet: (id, after) => api.get(`/live/${id}/chat`, { params: after ? { after } : {} }),
  liveChatBefore: (id, before) => api.get(`/live/${id}/chat`, { params: { before, limit: 40 } }),
  memberCount: () => api.get(`/stats/members`),
  adminOptOuts: (q = "") => api.get(`/admin/notifications/opt-outs`, { params: q ? { q } : {} }),
  liveChatPost: (id, text) => api.post(`/live/${id}/chat`, { text }),
  adminDeleteChat: (msgId) => api.delete(`/admin/live/chat/${msgId}`),
  adminPinChat: (msgId, pin) => api.post(`/admin/live/chat/${msgId}/pin`, { pin }),
  adminLiveStats: (id) => api.get(`/admin/live/${id}/stats`),

  // engagement reports
  adminEngagementReport: () => api.get(`/admin/reports/engagement`),
  adminEngagementCsv: () => api.get(`/admin/reports/engagement/export.csv`, { responseType: "blob" }),

  // voting (public)
  voteCampaigns: () => api.get(`/vote/campaigns`),
  voteCampaign: (id) => api.get(`/vote/campaigns/${id}`),
  castVote: (id, optionId, count) => api.post(`/vote/campaigns/${id}/vote`, { optionId, count }),

  // points wallet
  pointsBalance: () => api.get(`/points/balance`),
  pointsHistory: () => api.get(`/points/history`),
  pointsPurchase: (body) => api.post(`/points/purchase`, body),
  pointsMomoStatus: (ref) => api.get(`/points/momo/${ref}`),

  // voting (admin)
  adminVoteCampaigns: () => api.get(`/admin/vote/campaigns`),
  adminCreateCampaign: (b) => api.post(`/admin/vote/campaigns`, b),
  adminUpdateCampaign: (id, b) => api.put(`/admin/vote/campaigns/${id}`, b),
  adminDeleteCampaign: (id) => api.delete(`/admin/vote/campaigns/${id}`),
  adminCloseCampaign: (id) => api.post(`/admin/vote/campaigns/${id}/close`),
  adminAddOption: (id, b) => api.post(`/admin/vote/campaigns/${id}/options`, b),
  adminUpdateOption: (id, b) => api.put(`/admin/vote/options/${id}`, b),
  adminDeleteOption: (id) => api.delete(`/admin/vote/options/${id}`),
  adminApproveReward: (id, b) => api.post(`/admin/vote/options/${id}/approve-reward`, b || {}),
  adminMarkRewardPaid: (id, b) => api.post(`/admin/vote/options/${id}/mark-paid`, b || {}),
  adminVotesCsv: (id) => api.get(`/admin/vote/campaigns/${id}/export.csv`, { responseType: "blob" }),

  // manual subscription grant
  adminGrantSubscription: (id, b) => api.post(`/admin/users/${id}/grant-subscription`, b),

  // contestant signup + moderation
  contestantApply: (id, b) => api.post(`/vote/campaigns/${id}/apply`, b),
  uploadContestantPhoto: (image) => api.post(`/vote/upload-photo`, { image }),
  adminModerateOption: (id, status) => api.post(`/admin/vote/options/${id}/moderate`, { status }),
  adminRetryPayout: (id) => api.post(`/admin/vote/options/${id}/retry-payout`),
  adminPayouts: () => api.get(`/admin/vote/payouts`),
  adminMomoFloat: () => api.get(`/admin/momo/float`),

  // events & tickets (public)
  events: () => api.get(`/events`),
  event: (id) => api.get(`/events/${id}`),
  eventSeatmap: (id, categoryId) => api.get(`/events/${id}/seatmap`, { params: { categoryId } }),
  ticketsPurchase: (b) => api.post(`/tickets/purchase`, b),
  ticketsMomoStatus: (ref) => api.get(`/tickets/momo/${ref}`),
  myTickets: () => api.get(`/tickets/mine`),
  ticketByCode: (code) => api.get(`/tickets/${code}`),

  // events & tickets (admin)
  adminEvents: () => api.get(`/admin/events`),
  adminCreateEvent: (b) => api.post(`/admin/events`, b),
  adminUpdateEvent: (id, b) => api.put(`/admin/events/${id}`, b),
  adminDeleteEvent: (id) => api.delete(`/admin/events/${id}`),
  adminInvite: (id, b) => api.post(`/admin/events/${id}/invite`, b),
  adminScanTicket: (code) => api.post(`/admin/tickets/scan`, { code }),
  adminResendTicket: (code) => api.post(`/admin/tickets/${code}/resend`),
  adminCancelTicket: (code, reason) => api.post(`/admin/tickets/${code}/cancel`, { reason }),
  adminTicketsReport: (params) => api.get(`/admin/tickets/report`, { params }),
  adminEventStats: (id) => api.get(`/admin/events/${id}/stats`),
  adminEventTickets: (id) => api.get(`/admin/events/${id}/tickets`),

  // staff & permissions
  adminStaffModules: () => api.get(`/admin/staff/modules`),
  adminStaff: () => api.get(`/admin/staff`),
  adminCreateStaff: (b) => api.post(`/admin/staff`, b),
  adminUpdateStaff: (id, b) => api.put(`/admin/staff/${id}`, b),
  adminPromoteStaff: (id) => api.post(`/admin/staff/${id}/promote`),
  adminDemoteStaff: (id) => api.post(`/admin/staff/${id}/demote`),
  adminDeleteStaff: (id) => api.delete(`/admin/staff/${id}`),
  adminStaffLoginCode: (id) => api.post(`/admin/staff/${id}/login-code`),
  staffLogin: (b) => api.post(`/auth/staff/login`, b),
  staffVerifyOtp: (b) => api.post(`/auth/staff/verify-otp`, b),

  // approval workflow + activity
  adminNewsList: () => api.get(`/admin/news`),
  adminWorkflowPending: () => api.get(`/admin/workflow/pending`),
  adminWorkflowAction: (module, id, action, body) => api.post(`/admin/workflow/${module}/${id}/${action}`, body || {}),
  adminActivity: (params) => api.get(`/admin/activity`, { params }),

  // payments — needs attention
  adminPaymentsAttention: () => api.get(`/admin/payments/attention`),
  adminReconcilePayments: () => api.post(`/admin/payments/reconcile`),
  adminVerifyPayment: (id) => api.post(`/admin/payments/${id}/verify`),
  adminMarkPaid: (id, b) => api.post(`/admin/payments/${id}/mark-paid`, b),
  adminCancelPayment: (id, b) => api.post(`/admin/payments/${id}/cancel`, b),
  adminSmsUsage: (days) => api.get(`/admin/sms-usage`, { params: { days } }),
  adminApiAlerts: () => api.get(`/admin/api-alerts`),
  adminRecovery: (params) => api.get(`/admin/recovery`, { params }),
  adminRecoveryRestore: (id) => api.post(`/admin/recovery/${id}/restore`),
  adminRecoveryKeep: (b) => api.post(`/admin/recovery/keep`, b),
  adminApiMgmt: () => api.get(`/admin/api-management`),
  adminApiMgmtSave: (b) => api.put(`/admin/api-management`, b),
  adminApiTestSms: (b) => api.post(`/admin/api-management/test-sms`, b),
  adminMessageQueue: () => api.get(`/admin/message-queue`),
  adminMessageQueueClear: (b) => api.post(`/admin/message-queue/clear`, b),
  paymentMethods: (service) => api.get(`/payment-methods`, { params: { service } }),
  adminPaymentRetryMsg: (id) => api.post(`/admin/payments/${id}/retry-message`),
  adminPaymentRetryMsgAll: (days = 7) => api.post(`/admin/payments/retry-message-all`, { days }),

  // creators / celebrities (customer)
  creatorsList: (q = "", category = "") => api.get(`/creators`, { params: { q, category } }),
  creatorProfile: (slug) => api.get(`/creators/${slug}`),
  creatorChannelView: (slug, channel) => api.post(`/creators/${slug}/channel-view`, { channel }),
  creatorMyReport: () => api.get(`/creators/me/report`),
  adminCreatorReport: (id) => api.get(`/admin/creators/${id}/report`),
  creatorMe: () => api.get(`/creators/me`),
  creatorApply: (b) => api.post(`/creators/apply`, b),
  creatorUpdateMe: (b) => api.put(`/creators/me`, b),
  creatorConnectYoutube: (b) => api.post(`/creators/me/youtube`, b),
  creatorSync: () => api.post(`/creators/me/sync`),
  creatorAddVideo: (b) => api.post(`/creators/me/videos`, b),
  creatorDeleteVideo: (id) => api.delete(`/creators/me/videos/${id}`),
  creatorClaimReward: () => api.post(`/creators/me/claim-reward`),
  creatorMyClaims: () => api.get(`/creators/me/claims`),
  creatorVideo: (id) => api.get(`/creator-videos/${id}`),
  creatorVideoView: (id, watchSeconds) => api.post(`/creator-videos/${id}/view`, { watchSeconds }),
  creatorVideoComments: (id) => api.get(`/creator-videos/${id}/comments`),
  creatorVideoAddComment: (id, text) => api.post(`/creator-videos/${id}/comments`, { text }),

  // creators (admin)
  adminCreators: () => api.get(`/admin/creators`),
  adminSetCreatorStatus: (id, status) => api.post(`/admin/creators/${id}/status`, { status }),
  adminCreatorRewardConfig: () => api.get(`/admin/creators/reward-config`),
  adminSetCreatorRewardConfig: (b) => api.put(`/admin/creators/reward-config`, b),
  adminCreatorClaims: () => api.get(`/admin/creator-claims`),
  adminApproveCreatorClaim: (id, b) => api.post(`/admin/creator-claims/${id}/approve`, b || {}),
  adminRejectCreatorClaim: (id, b) => api.post(`/admin/creator-claims/${id}/reject`, b || {}),
  adminRetryCreatorClaim: (id) => api.post(`/admin/creator-claims/${id}/retry`),
  adminMarkCreatorClaimPaid: (id, b) => api.post(`/admin/creator-claims/${id}/mark-paid`, b || {}),

  // football (public)
  footballCompetitions: () => api.get(`/football/competitions`),
  footballCompetition: (slug) => api.get(`/football/competitions/${slug}`),
  footballStandings: (slug, seasonId = "") => api.get(`/football/competitions/${slug}/standings`, { params: { seasonId } }),
  footballFixtures: (slug, seasonId = "") => api.get(`/football/competitions/${slug}/fixtures`, { params: { seasonId } }),
  footballResults: (slug, seasonId = "") => api.get(`/football/competitions/${slug}/results`, { params: { seasonId } }),
  footballScorers: (slug) => api.get(`/football/competitions/${slug}/scorers`),
  footballLive: () => api.get(`/football/live`),
  footballMatch: (fixtureId) => api.get(`/football/fixtures/${fixtureId}`),
  footballMatches: (date, leagues = "") => api.get(`/football/matches`, { params: { date, leagues } }),
  footballHighlights: (fixtureId = "", limit = 12) => api.get(`/football/highlights`, { params: { fixtureId, limit } }),
  sbLive: (q = "") => api.get(`/sb/live`, { params: { q } }),
  sbReplays: (comp = "", q = "") => api.get(`/sb/replays`, { params: { comp, q } }),
  broadcasterMe: () => api.get(`/broadcaster/me`),
  broadcasterReset: () => api.post(`/broadcaster/me/reset-key`),
  cfBroadcasters: () => api.get(`/admin/cf/broadcasters`),
  cfSetBroadcaster: (phone, on = true) => api.post(`/admin/cf/broadcasters`, { phone, on }),
  cfLive: () => api.get(`/admin/cf/live`),
  cfLiveCreate: (b) => api.post(`/admin/cf/live`, b),
  cfLiveUpdate: (uid, b) => api.patch(`/admin/cf/live/${uid}`, b),
  cfLiveDelete: (uid) => api.delete(`/admin/cf/live/${uid}`),
  cfRecordings: (uid) => api.get(`/admin/cf/live/${uid}/recordings`),
  cfVideos: () => api.get(`/admin/cf/videos`),
  cfUploadUrl: (b) => api.post(`/admin/cf/videos/upload`, b),
  cfCopy: (b) => api.post(`/admin/cf/videos/copy`, b),
  rwTicker: () => api.get(`/rwanda/ticker`),
  rwScorers: () => api.get(`/rwanda/topscorers`),
  topScorers: (league = "39") => api.get(`/football/topscorers`, { params: { league } }),
  sbCompetitions: (q = "") => api.get(`/sb/competitions`, { params: { q } }),
  sbTeams: (q = "") => api.get(`/sb/teams`, { params: { q } }),
  sbCompetition: (slug) => api.get(`/sb/competition/${slug}`),
  sbTeam: (slug) => api.get(`/sb/team/${slug}`),
  sbWatch: (videoId) => api.post(`/sb/watch`, { videoId }),
  highlights: (limit = 60) => api.get(`/highlights`, { params: { limit } }),
  myFbFavs: () => api.get(`/me/football-favourites`),
  saveFbFavs: (b) => api.put(`/me/football-favourites`, b),
  analyticsHit: (b) => api.post(`/analytics/hit`, b),
  utilCatalog: () => api.get(`/utilities/catalog`),
  utilPaypal: (b) => api.post(`/utilities/orders/paypal`, b),
  utilCardQuote: (amount) => api.get(`/utilities/card-quote`, { params: { amount } }),
  utilValidate: (b) => api.post(`/utilities/validate`, b),
  utilOrder: (b) => api.post(`/utilities/orders`, b),
  utilOrderGet: (id) => api.get(`/utilities/orders/${id}`),
  utilMy: () => api.get(`/utilities/my`),
  utilSms: (id) => api.post(`/utilities/orders/${id}/sms`),
  adminUtilSettings: () => api.get(`/admin/utilities/settings`),
  adminUtilSaveSettings: (b) => api.put(`/admin/utilities/settings`, b),
  adminUtilBalance: () => api.get(`/admin/utilities/balance`),
  adminUtilOrders: (params) => api.get(`/admin/utilities/orders`, { params }),
  adminUtilRefunded: (id) => api.post(`/admin/utilities/orders/${id}/refunded`),
  adminUtilRecheck: (id) => api.post(`/admin/utilities/orders/${id}/recheck`),
  adminUtilPayments: (params) => api.get(`/admin/utilities/payments`, { params }),
  utilCommissions: (params) => api.get(`/utilities/commissions`, { params }),
  adminUtilPayVerify: (id) => api.post(`/admin/utilities/payments/${id}/verify`),
  adminUtilPayConfirm: (id, note) => api.post(`/admin/utilities/payments/${id}/confirm`, { note }),
  adminUtilPayChecks: (id) => api.get(`/admin/utilities/payments/${id}/checks`),
  rwLocations: (params) => api.get(`/locations/rw`, { params }),
  mapsConfig: () => api.get(`/config/maps`),
  merchantDirectory: (params) => api.get(`/merchants/directory`, { params }),
  merchantBranding: (kind, file) => { const f = new FormData(); f.append("file", file); return api.post(`/merchants/me/branding`, f, { params: { kind }, timeout: 120000 }); },
  followedTeams: () => api.get(`/me/followed-teams`),
  setFollowedTeams: (followTeams) => api.put(`/me/followed-teams`, { followTeams }),
  adminMerchantSummary: (b = {}) => api.post(`/admin/merchants/summary/send`, b, { timeout: 60000 }),
  merchantUploadDoc: (file) => { const f = new FormData(); f.append("file", file); return api.post(`/merchants/document`, f, { timeout: 120000 }); },
  merchantRegister: (b) => api.post(`/merchants/register`, b, { timeout: 60000 }),
  merchantMe: () => api.get(`/merchants/me`),
  merchantVerifyAgain: () => api.post(`/merchants/me/verify-again`, {}, { timeout: 60000 }),
  merchantMyPayments: () => api.get(`/merchants/me/payments`),
  merchantLookup: (code, amount = 0) => api.get(`/merchants/lookup/${code}`, { params: { amount } }),
  merchantPay: (b) => api.post(`/merchants/pay`, b, { timeout: 60000 }),
  merchantPayStatus: (id) => api.get(`/merchants/payments/${id}`),
  merchantCustomerHistory: () => api.get(`/merchants/my-payments`),
  adminMerchants: (params) => api.get(`/admin/merchants`, { params }),
  adminMerchantDoc: (id) => api.get(`/admin/merchants/${id}/document`, { responseType: "blob", timeout: 60000 }),
  adminMerchantDecision: (id, b) => api.post(`/admin/merchants/${id}/decision`, b),
  adminMerchantsReport: (params) => api.get(`/admin/merchants-report`, { params }),
  myByline: () => api.get(`/admin/me/byline`),
  setMyByline: (b) => api.put(`/admin/me/byline`, b),
  newsVideoInit: (b) => api.post(`/admin/news/media/video/init`, b),
  newsVideoChunk: (id, blob) => api.put(`/admin/news/media/video/${id}/chunk`, blob, { headers: { "Content-Type": "application/octet-stream" } }),
  newsVideoFinalize: (id) => api.post(`/admin/news/media/video/${id}/finalize`),
  adminClientErrors: () => api.get(`/admin/client-errors`),
  previewState: (stream) => api.get(`/preview/${stream}`),
  previewStart: (stream) => api.post(`/preview/${stream}/start`),
  adminDailyVisitors: (day) => api.get(`/admin/analytics/visitors/daily`, { params: { day } }),
  adminDailyVisitorsSend: (day) => api.post(`/admin/analytics/visitors/daily/send`, { day }),
  adminVisitors: (days) => api.get(`/admin/analytics/visitors`, { params: { days } }),
  pushSubscribeGuest: (b) => api.post(`/push/subscribe-guest`, b),
  pushTestMe: (endpoint) => api.post(`/push/test-me`, { endpoint }),
  adminPushStats: () => api.get(`/admin/push/stats`),
  adminPushSend: (b) => api.post(`/admin/push/send`, b),
  radioHeartbeat: (sid, playing) => api.post(`/radio/heartbeat`, { sid, playing }),
  radioListeners: () => api.get(`/radio/listeners`),
  adminRadioListeners: () => api.get(`/admin/radio/listeners`),
  adminSplits: (params) => api.get(`/admin/splits`, { params }),
  adminSplitSettle: (id, b) => api.post(`/admin/splits/${id}/settle`, b),
  adminSplitClawback: (id, b) => api.post(`/admin/splits/${id}/clawback`, b),
  adminSplitReconcile: (b) => api.post(`/admin/splits/reconcile`, b),
  adminSplitConfig: (b) => api.put(`/admin/splits/config`, b),
  eaOverview: () => api.get(`/eafrica/overview`),
  eaMatches: (date, competitions = "", scope = "ea") => api.get(`/eafrica/matches`, { params: { date, competitions, scope } }),
  eaLive: (competitions = "", scope = "ea") => api.get(`/eafrica/live`, { params: { competitions, scope } }),
  lsWorld: () => api.get(`/lsworld/overview`),
  lsCompetitions: (params) => api.get(`/lsworld/competitions`, { params }),
  eaMatch: (key) => api.get(`/eafrica/match/${key}`),
  eaStandings: (cid, scope = "ea") => api.get(`/eafrica/standings/${cid}`, { params: { scope } }),
  adminLivescoreStatus: () => api.get(`/admin/livescore/status`),
  adminLivescoreSave: (b) => api.put(`/admin/livescore/keys`, b),
  adminLivescoreRemove: () => api.delete(`/admin/livescore/keys`),
  adminVimeoStatus: () => api.get(`/admin/vimeo/status`),
  adminVimeoSaveToken: (token) => api.put(`/admin/vimeo/token`, { token }),
  adminVimeoRemoveToken: () => api.delete(`/admin/vimeo/token`),
  adminVimeoLiveList: () => api.get(`/admin/vimeo/live`),
  adminVimeoLiveCreate: (title) => api.post(`/admin/vimeo/live`, { title }),
  adminVimeoLiveIngest: (id) => api.get(`/admin/vimeo/live/${id}/ingest`),
  adminVimeoLiveDelete: (id) => api.delete(`/admin/vimeo/live/${id}`),
  adminVimeoLiveCapability: () => api.get(`/admin/vimeo/live/capability`),
  adminVimeoLibrary: (page = 1) => api.get(`/admin/vimeo/library`, { params: { page } }),
  adminVimeoImport: (b) => api.post(`/admin/vimeo/import`, b),
  adminVimeoMoveStatus: () => api.get(`/admin/vimeo/move`),
  adminVimeoMoveStart: () => api.post(`/admin/vimeo/move`),
  adminFbHighlights: () => api.get(`/admin/football/highlights`),
  hlCatalog: () => api.get(`/highlights/catalog`),
  hlResults: (league) => api.get(`/highlights/results`, { params: { league } }),
  sbAdminStatus: () => api.get(`/admin/scorebat/status`),
  sbAdminSaveKey: (slot, value) => api.put(`/admin/scorebat/keys`, { slot, value }),
  sbAdminDisplay: (hide) => api.put(`/admin/scorebat/display`, { hide }),
  sbAdminClearKey: (slot) => api.delete(`/admin/scorebat/keys/${slot}`),
  adminFbCreateHighlight: (b) => api.post(`/admin/football/highlights`, b),
  adminFbUpdateHighlight: (id, b) => api.put(`/admin/football/highlights/${id}`, b),
  adminFbDeleteHighlight: (id) => api.delete(`/admin/football/highlights/${id}`),
  footballTeam: (id) => api.get(`/football/teams/${id}`),
  footballPlayer: (id) => api.get(`/football/players/${id}`),
  footballCompTeams: (slug) => api.get(`/football/competitions/${slug}/teams`),

  // football (admin)
  adminFbCompetitions: () => api.get(`/admin/football/competitions`),
  adminFbCreateCompetition: (b) => api.post(`/admin/football/competitions`, b),
  adminFbUpdateCompetition: (id, b) => api.put(`/admin/football/competitions/${id}`, b),
  adminFbDeleteCompetition: (id) => api.delete(`/admin/football/competitions/${id}`),
  adminFbSeasons: (cid) => api.get(`/admin/football/competitions/${cid}/seasons`),
  adminFbCreateSeason: (cid, b) => api.post(`/admin/football/competitions/${cid}/seasons`, b),
  adminFbUpdateSeason: (id, b) => api.put(`/admin/football/seasons/${id}`, b),
  adminFbDeleteSeason: (id) => api.delete(`/admin/football/seasons/${id}`),
  adminFbTeams: (cid) => api.get(`/admin/football/competitions/${cid}/teams`),
  adminFbCreateTeam: (cid, b) => api.post(`/admin/football/competitions/${cid}/teams`, b),
  adminFbUpdateTeam: (id, b) => api.put(`/admin/football/teams/${id}`, b),
  adminFbDeleteTeam: (id) => api.delete(`/admin/football/teams/${id}`),
  adminFbFixtures: (cid, seasonId = "") => api.get(`/admin/football/competitions/${cid}/fixtures`, { params: { seasonId } }),
  adminFbCreateFixture: (cid, b) => api.post(`/admin/football/competitions/${cid}/fixtures`, b),
  adminFbUpdateFixture: (id, b) => api.put(`/admin/football/fixtures/${id}`, b),
  adminFbEnterResult: (id, b) => api.post(`/admin/football/fixtures/${id}/result`, b),
  adminFbDeleteFixture: (id) => api.delete(`/admin/football/fixtures/${id}`),
  adminFbAdjustments: (cid, seasonId = "") => api.get(`/admin/football/competitions/${cid}/adjustments`, { params: { seasonId } }),
  adminFbAddAdjustment: (cid, b) => api.post(`/admin/football/competitions/${cid}/adjustments`, b),
  adminFbDeleteAdjustment: (id) => api.delete(`/admin/football/adjustments/${id}`),
  adminFbProviderConfig: () => api.get(`/admin/football/provider-config`),
  adminFbStandingsPreview: (cid, seasonId) => api.get(`/admin/football/standings-preview`, { params: { cid, seasonId } }),

  // system guide
  adminSystemGuide: () => api.get(`/admin/system-guide.pdf`, { responseType: "blob" }),

  // VOD (Cloudflare Stream)
  vodConfig: () => api.get(`/vod/config`),
  vodLibrary: (category = "", q = "") => api.get(`/vod/library`, { params: { category, q } }),
  vodCategories: () => api.get(`/vod/categories`),
  vodDetail: (id) => api.get(`/vod/${id}`),
  vodView: (id) => api.post(`/vod/${id}/view`),
  vodPlaybackToken: (id) => api.get(`/vod/${id}/playback-token`),
  vodBrowse: (category = "", range = "7d", date = "") => api.get(`/vod/browse`, { params: { category, range, date } }),
  vodBrowseCategories: () => api.get(`/vod/browse-categories`),

  // Generic media (image/video <=100MB) + homepage video + advertisements
  mediaInit: (b) => api.post("/admin/media/init", b),
  mediaChunk: (id, blob) => api.post(`/admin/media/${id}/chunk`, blob, { headers: { "Content-Type": "application/octet-stream" } }),
  mediaFinalize: (id) => api.post(`/admin/media/${id}/finalize`),
  homepageMedia: () => api.get("/homepage-media"),
  adminHomepageMedia: () => api.get("/admin/homepage-media"),
  setHomepageMedia: (b) => api.put("/admin/homepage-media", b),
  publicAds: (placement) => api.get("/ads", { params: { placement } }),
  adImpression: (id) => api.post(`/ads/${id}/impression`),
  adClick: (id) => api.post(`/ads/${id}/click`),
  adminAds: () => api.get("/admin/ads"),
  adminAdCreate: (b) => api.post("/admin/ads", b),
  adminAdUpdate: (id, b) => api.put(`/admin/ads/${id}`, b),
  adminAdDelete: (id) => api.delete(`/admin/ads/${id}`),

  // Notifications & push
  pushVapidPublic: () => api.get("/push/vapid-public"),
  pushSubscribe: (subscription, ua) => api.post("/push/subscribe", { subscription, ua }),
  pushUnsubscribe: (endpoint) => api.post("/push/unsubscribe", { endpoint }),
  myNotificationSettings: () => api.get("/me/notification-settings"),
  updateMyNotificationSettings: (b) => api.put("/me/notification-settings", b),
  notifPreview: (type, refId) => api.get(`/admin/notifications/preview`, { params: { type, refId } }),
  notifAudienceCounts: () => api.get("/admin/notifications/audience-counts"),
  notifSend: (b) => api.post("/admin/notifications/send", b),
  notifResend: (id) => api.post(`/admin/notifications/${id}/resend`),
  notifHistory: (limit = 100) => api.get(`/admin/notifications`, { params: { limit } }),
  // admin vod
  adminVodUploadUrl: (b) => api.post(`/admin/vod/upload-url`, b),
  adminVodList: () => api.get(`/admin/vod`),
  adminVodSync: (id) => api.post(`/admin/vod/${id}/sync`),
  adminVodEdit: (id, b) => api.put(`/admin/vod/${id}`, b),
  adminVodPublish: (id, published) => api.post(`/admin/vod/${id}/publish`, { published }),
  adminVodApprove: (id) => api.post(`/admin/vod/${id}/approve`),
  adminVodReject: (id, reason) => api.post(`/admin/vod/${id}/reject`, { reason }),
  adminVodDelete: (id) => api.delete(`/admin/vod/${id}`),
  // creator vod
  creatorVodUploadUrl: (b) => api.post(`/creators/vod/upload-url`, b),
  creatorVodMine: () => api.get(`/creators/vod/mine`),
  creatorVodSync: (id) => api.post(`/creators/vod/${id}/sync`),
  creatorVodDelete: (id) => api.delete(`/creators/vod/${id}`),
  // direct upload to our storage (chunked)
  adminVodDirectInit: (b) => api.post(`/admin/vod/direct/init`, b),
  adminVodDirectChunk: (id, blob) => api.put(`/admin/vod/direct/${id}/chunk`, blob, { headers: { "Content-Type": "application/octet-stream" } }),
  adminVodDirectFinalize: (id) => api.post(`/admin/vod/direct/${id}/finalize`),
  creatorVodDirectInit: (b) => api.post(`/creators/vod/direct/init`, b),
  creatorVodDirectChunk: (id, blob) => api.put(`/creators/vod/direct/${id}/chunk`, blob, { headers: { "Content-Type": "application/octet-stream" } }),
  creatorVodDirectFinalize: (id) => api.post(`/creators/vod/direct/${id}/finalize`),
};

export async function fetchAllShows(pageSize = 40, maxPages = 12) {
  let all = [];
  for (let i = 0; i < maxPages; i++) {
    const { data } = await endpoints.shows(pageSize, i * pageSize);
    if (!Array.isArray(data) || data.length === 0) break;
    all = all.concat(data);
    if (data.length < pageSize) break;
  }
  return all;
}

export function apiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return detail?.msg || String(detail);
}
